import React, { SyntheticEvent, useEffect, useState } from "react";
import Header from "../../components/Header/Header";
import Footer from "../../components/Footer/Footer";
import {
  Autocomplete,
  Button,
  Card,
  CardContent,
  CardHeader,
  Grid,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import {
  DataGrid,
  GridActionsCellItem,
  GridColDef,
  GridEventListener,
  GridRenderEditCellParams,
  GridRowEditStopReasons,
  GridRowId,
  GridRowModel,
  GridRowModes,
  GridRowModesModel,
  GridRowParams,
} from "@mui/x-data-grid";
import CreateEmployee from "../../components/Management/CreateEmployeeButton";
import axiosConfig from "../../axiosConfig";
import { jaJP } from "@mui/x-data-grid/locales";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Cancel";
import DeleteEmployeeButton from "../../components/Management/DeleteEmployeeButton";

interface SearchInput {
  fullname: string;
  username: string;
  [key: string]: string;
}

const SubcontractorManagement: React.FC = () => {
  const columns: GridColDef[] = [
    { field: "id", headerName: "ID", width: 120 },
    {
      field: "fullname",
      headerName: "氏名",
      width: 300,
    },
    { field: "username", headerName: "ログインID", width: 250 },
    {
      field: "email",
      headerName: "メールアドレス",
      width: 280,
    },
    {
      field: "phone_number",
      headerName: "電話番号",
      width: 200,
    },
    {
      field: "type",
      headerName: "権限",
      width: 190,
    },
    {
      field: "capacity",
      headerName: "1日処理可能枚数",
      width: 200,
      editable: true,
      type: "number",
    },
    {
      field: "working_start_time",
      headerName: "外注先の始業時間",
      width: 200,
      editable: true,
      renderEditCell: (params: GridRenderEditCellParams) => {
        return (
          <input
            type="time"
            style={{
              padding: 6,
            }}
            value={params.value}
            onChange={(e) => {
              params.api.setEditCellValue({
                id: params.id,
                field: params.field,
                value: e.target.value,
              });
            }}
          />
        );
      },
    },
    {
      field: "working_end_time",
      headerName: "外注先の終業時間",
      width: 200,
      editable: true,
      renderEditCell: (params: GridRenderEditCellParams) => {
        return (
          <input
            type="time"
            style={{
              padding: 6,
            }}
            value={params.value}
            onChange={(e) => {
              params.api.setEditCellValue({
                id: params.id,
                field: params.field,
                value: e.target.value,
              });
            }}
          />
        );
      },
    },
    {
      field: "actions",
      type: "actions",
      headerName: "操作",
      width: 150,
      cellClassName: "actions",
      getActions: (params: GridRowParams) => {
        const isInEditMode =
          rowModesModel[params.id]?.mode === GridRowModes.Edit;

        if (isInEditMode) {
          return [
            <GridActionsCellItem
              icon={<SaveIcon />}
              label="Save"
              sx={{
                color: "primary.main",
              }}
              onClick={handleSaveClick(params)}
            />,
            <GridActionsCellItem
              icon={<CancelIcon />}
              label="Cancel"
              sx={{
                color: "error.main",
              }}
              className="textPrimary"
              onClick={handleCancelClick(params.id)}
              color="inherit"
            />,
          ];
        }

        return [
          <GridActionsCellItem
            icon={<EditIcon color="primary" />}
            label="Edit"
            className="textPrimary"
            onClick={handleEditClick(params.id)}
            color="inherit"
          />,
          <DeleteEmployeeButton
            handleFetchData={() => fetchData()}
            detail={params.row}
          />,
        ];
      },
    },
  ];
  const [data, setData] = useState<any[]>([]);
  const [showSearchContent, setShowSearchContent] = useState(true);
  const [searchInput, setSearchInput] = useState<Partial<SearchInput>>({
    fullname: "",
    username: "",
  });
  const [fullnameList, setFullnameList] = useState<string[]>([]);
  const [usernameList, setUsernameList] = useState<string[]>([]);

  // Start edit grid data table
  const [rowModesModel, setRowModesModel] = useState<GridRowModesModel>({});
  const handleEditClick = (id: GridRowId) => () => {
    setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.Edit } });
  };
  const handleSaveClick = (params: GridRowParams) => async () => {
    setRowModesModel({
      ...rowModesModel,
      [params.id]: { mode: GridRowModes.View },
    });
  };
  const handleCancelClick = (id: GridRowId) => () => {
    setRowModesModel({
      ...rowModesModel,
      [id]: { mode: GridRowModes.View, ignoreModifications: true },
    });
  };
  const processRowUpdate = (newRow: GridRowModel) => {
    axiosConfig({
      method: "patch",
      url: "/settings/subcontractor-capacity",
      data: {
        subcontractor_id: newRow.id,
        capacity: newRow.capacity,
      },
    });
    axiosConfig({
      method: "patch",
      url: "/settings/subcontractor-working-time",
      data: {
        subcontractor_id: newRow.id,
        working_start_time: newRow.working_start_time,
        working_end_time: newRow.working_end_time,
      },
    });
    setData(data.map((row) => (row.id === newRow.id ? newRow : row)));
    return newRow;
  };
  const handleRowModesModelChange = (newRowModesModel: GridRowModesModel) => {
    setRowModesModel(newRowModesModel);
  };
  const handleRowEditStop: GridEventListener<"rowEditStop"> = (
    params,
    event
  ) => {
    if (params.reason === GridRowEditStopReasons.rowFocusOut) {
      event.defaultMuiPrevented = true;
    }
  };
  // End edit grid data table

  const fetchData = async (search = "") => {
    let url = "/users/subcontractor";
    if (search !== "") {
      url += `?${search}`;
    }
    await axiosConfig.get(url).then((res) => {
      setFullnameList(res.data.body.data.map((item: any) => item.fullname));
      setUsernameList(res.data.body.data.map((item: any) => item.username));
      setData(res.data.body.data);
    });
  };

  const handleChangeSearchInput = (
    e: SyntheticEvent,
    value: string,
    field: string
  ) => {
    setSearchInput((prevData) => ({
      ...prevData,
      [field]: value,
    }));
  };

  const handleSearch = async () => {
    let searchUrl = "";
    const keys = Object.keys(searchInput);
    keys.forEach((key) => {
      if (searchInput[key] !== "") {
        if (searchUrl.length === 0) searchUrl += `${key}=${searchInput[key]}`;
        else searchUrl += `&${key}=${searchInput[key]}`;
      }
    });
    await fetchData(searchUrl);
  };

  useEffect(() => {
    fetchData();
  }, []);

  return (
    <div className="flex flex-col">
      <Header pageTitle="外注先アカウント管理"></Header>
      <Grid
        className="pt-4 pb-14"
        height={"90vh"}
        sx={{ overflow: "hidden", overflowY: "scroll" }}
      >
        <Card
          className="mx-auto"
          sx={{ border: 1, borderColor: "grey.300", width: "90vw" }}
        >
          <CardHeader
            sx={{ bgcolor: "#F5F5F5", paddingY: "5px" }}
            subheader={
              <Button
                variant="text"
                sx={{ padding: 0, color: "black" }}
                disableRipple={true}
                onClick={() => setShowSearchContent(!showSearchContent)}
              >
                絞り込み
              </Button>
            }
          />
          {showSearchContent && (
            <CardContent sx={{ paddingY: "5px" }}>
              <Grid container spacing={2}>
                <Grid item xs={2}>
                  <h3>氏名</h3>
                  <Autocomplete
                    freeSolo
                    className="mt-1"
                    size="small"
                    disableClearable
                    renderInput={(params) => <TextField {...params} />}
                    options={fullnameList}
                    value={searchInput.fullname}
                    onInputChange={(e, value) =>
                      handleChangeSearchInput(e, value, "fullname")
                    }
                  />
                </Grid>
                <Grid item xs={2}>
                  <h3>ログインID</h3>
                  <Autocomplete
                    freeSolo
                    className="mt-1"
                    size="small"
                    disableClearable
                    renderInput={(params) => <TextField {...params} />}
                    options={usernameList}
                    value={searchInput.username}
                    onInputChange={(e, value) =>
                      handleChangeSearchInput(e, value, "username")
                    }
                  />
                </Grid>
              </Grid>
              <Stack className="mt-2">
                <div className="ml-auto">
                  <Button
                    variant="contained"
                    sx={{ paddingX: "30px" }}
                    onClick={handleSearch}
                  >
                    検索
                  </Button>
                </div>
              </Stack>
            </CardContent>
          )}
        </Card>
        <Card
          className="mx-auto mt-5 mb-4"
          sx={{ width: "90vw", border: 1, borderColor: "grey.300" }}
        >
          <CardHeader
            sx={{ bgcolor: "#F5F5F5", padding: "12px" }}
            subheader={
              <CreateEmployee
                handleFetchData={fetchData}
                type="subcontractor"
              />
            }
          />
          <CardContent>
            <DataGrid
              localeText={jaJP.components.MuiDataGrid.defaultProps.localeText}
              rows={data}
              columns={columns}
              editMode="row"
              rowModesModel={rowModesModel}
              onRowModesModelChange={handleRowModesModelChange}
              onRowEditStop={handleRowEditStop}
              processRowUpdate={processRowUpdate}
              slotProps={{
                toolbar: { setData, setRowModesModel },
              }}
              initialState={{
                pagination: {
                  paginationModel: { page: 0, pageSize: 10 },
                },
              }}
              pageSizeOptions={[10, 20, 30]}
              disableColumnMenu
              disableColumnSorting
              disableRowSelectionOnClick
            />
            {data.length === 0 && (
              <Typography variant="subtitle1" align="center" sx={{ mt: 2 }}>
                データがありません
              </Typography>
            )}
          </CardContent>
        </Card>
      </Grid>
      <Footer></Footer>
    </div>
  );
};
export default SubcontractorManagement;
